import type { AdhocSession } from '../../api/adhocApi';
import type { HealthProfile, ProfileSex, ProfileSmoking } from '../../utils/userProfile';
import { isProfileComplete } from '../../utils/userProfile';

const STORAGE_KEY = 'adhoc_flow_v1';

export type PersistedStage = 'confirming_payment' | 'disclaimer' | 'profile' | 'scan_instructions' | 'scanning';

export interface AdhocFlowSnapshot {
  sessionId: string;
  adhocId: string;
  stage: PersistedStage;
  sessionEmail?: string;
  guestName?: string;
  healthProfile?: Partial<HealthProfile>;
}

export function readAdhocFlowSnapshot(): AdhocFlowSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdhocFlowSnapshot;
    if (!parsed?.sessionId || !parsed?.adhocId || !parsed?.stage) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeAdhocFlowSnapshot(snapshot: AdhocFlowSnapshot): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota / private mode */
  }
}

export function clearAdhocFlowSnapshot(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function asProfileSex(value: string | null): ProfileSex | null {
  if (value === 'male' || value === 'female' || value === 'unspecified') return value;
  return null;
}

function asSmoking(value: string | null): ProfileSmoking | null {
  if (value === 'smoker' || value === 'non_smoker' || value === 'unspecified') return value;
  return null;
}

/** Rebuild SDK health profile from a paid session returned by the API. */
export function sessionToHealthProfile(session: AdhocSession): Partial<HealthProfile> | null {
  const profile: Partial<HealthProfile> = {
    sex: asProfileSex(session.sex),
    date_of_birth: session.date_of_birth,
    age: session.age,
    height_cm: session.height_cm,
    weight_kg: session.weight_kg,
    smoking_status: asSmoking(session.smoking_status),
  };
  profile.profile_complete = isProfileComplete(profile);
  return profile;
}
